import { styled } from '@classify/react-core-components/styles';
import ClearIcon from '@mui/icons-material/Clear';
import DoneIcon from '@mui/icons-material/Done';
import RotateRightIcon from '@mui/icons-material/RotateRight';
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedIn';

import CircleArrowIcon from '@openclassrooms/ui-web/icons/CircleArrow';
import { STATUSES } from '@openclassrooms/ui-web/learning-activities';
import { translate } from '@openclassrooms/translations';
import LearningStatus from './LearningStatus';
import { QUIZ_STATUSES, STATUS_PROMOTION } from '../../constants/LearningStatuses';

const { STATUS_NOT_STARTED, STATUS_IN_PROGRESS, STATUS_CAN_RETRY, STATUS_FAILED, STATUS_PASSED } =
  STATUSES;

interface Props {
  status: (typeof QUIZ_STATUSES)[number];
  color?: string;
  ariaHidden?: boolean;
}

const NotStartedIcon = styled(AssignmentTurnedInIcon)(({ theme: { palette } }) => ({
  width: 14,
  height: 14,
  color: palette.progress.completion.disabled.active,
}));

const PromotionIcon = styled(AssignmentTurnedInIcon)({
  width: 14,
  height: 14,
});

const InProgressIcon = styled(RotateRightIcon)(({ theme: { palette } }) => ({
  width: 18,
  height: 18,
  color: palette.progress.event.completed,
}));

const CanRetryIcon = styled(CircleArrowIcon)(({ theme: { palette } }) => ({
  width: 12,
  height: 12,
  color: palette.progress.event.awaitingFeedback,
}));

const FailedIcon = styled(ClearIcon)(({ theme: { palette } }) => ({
  width: 14,
  height: 14,
  color: palette.progress.event.noShow,
}));

const PassedIcon = styled(DoneIcon)(({ theme: { palette } }) => ({
  width: 14,
  height: 14,
  color: palette.progress.learningAsset.onPassed,
}));

const getLearningStatusIcon = (color: string | undefined, ariaHidden: boolean) => ({
  [STATUS_NOT_STARTED]: (
    <LearningStatus
      sx={{
        borderColor: 'progress.completion.disabled.active',
      }}
    >
      <NotStartedIcon
        aria-label={translate('courses.accessibility_labels.quiz.not_started')}
        aria-hidden={ariaHidden}
      />
    </LearningStatus>
  ),
  [STATUS_IN_PROGRESS]: (
    <LearningStatus
      sx={{
        borderColor: 'progress.event.completed',
      }}
    >
      <InProgressIcon
        aria-label={translate('courses.accessibility_labels.quiz.in_progress')}
        aria-hidden={ariaHidden}
      />
    </LearningStatus>
  ),
  [STATUS_CAN_RETRY]: (
    <LearningStatus
      sx={{
        borderColor: 'progress.event.awaitingFeedback',
      }}
    >
      <CanRetryIcon
        aria-label={translate('courses.accessibility_labels.quiz.retry')}
        aria-hidden={ariaHidden}
      />
    </LearningStatus>
  ),
  [STATUS_FAILED]: (
    <LearningStatus
      sx={{
        borderColor: 'progress.event.noShow',
      }}
    >
      <FailedIcon
        aria-label={translate('courses.accessibility_labels.quiz.failed')}
        aria-hidden={ariaHidden}
      />
    </LearningStatus>
  ),
  [STATUS_PASSED]: (
    <LearningStatus
      sx={{
        borderColor: 'progress.learningAsset.background.passed',
        backgroundColor: 'progress.learningAsset.background.passed',
      }}
    >
      <PassedIcon
        aria-label={translate('courses.accessibility_labels.quiz.passed')}
        aria-hidden={ariaHidden}
      />
    </LearningStatus>
  ),
  [STATUS_PROMOTION]: (
    <LearningStatus
      sx={{
        borderColor: color,
      }}
    >
      <PromotionIcon
        htmlColor={color}
        aria-label={translate('courses.accessibility_labels.quiz.promotion')}
        aria-hidden={ariaHidden}
      />
    </LearningStatus>
  ),
});

const QuizLearningStatus = ({ status, color = '', ariaHidden = false }: Props) =>
  getLearningStatusIcon(color, ariaHidden)[status];

export default QuizLearningStatus;
